import { FunctionComponent } from 'react';

import type { SetTableTag } from '../../types/KidPdfGeneratorV2.types';
import type { PresetKidMarkersContext } from '../../hooks/usePresetKidMarkers';
import { CompositionOfCostsTable } from './presetTables/CompositionOfCostsTable';
import { CostsOverTimeTable } from './presetTables/CostsOverTimeTable';
import { PerformanceScenariosTable } from './presetTables/PerformanceScenariosTable';
import { PresetTableShell } from './presetTables/PresetTableShell';
import { PRESET_TABLE_EMPTY_STATE } from './presetTables/presetTableEmptyState';
import type { SetTableViewProps } from './presetTables/useSeededKidSetTable';
import { useSeededKidSetTable } from './presetTables/useSeededKidSetTable';

interface KidSetTableInputProps {
  index: number;
  tag: SetTableTag;
  markerContext?: PresetKidMarkersContext;
}

const SET_TABLE_COMPONENTS: Record<
  SetTableTag,
  FunctionComponent<SetTableViewProps>
> = {
  costs_over_time_table: CostsOverTimeTable,
  composition_of_costs_table: CompositionOfCostsTable,
  performance_scenarios_table: PerformanceScenariosTable,
};

/**
 * Editor wrapper for the preset KID set tables: useSeededKidSetTable seeds the
 * form element; the table components are purely presentational.
 */
export const KidSetTableInput: FunctionComponent<KidSetTableInputProps> = ({
  index,
  tag,
  markerContext,
}) => {
  const {
    label,
    source,
    content,
    updateCell,
    isInjectedCell,
    isLoading,
    hasMarkers,
  } = useSeededKidSetTable(index, tag, markerContext);

  const TableComponent = SET_TABLE_COMPONENTS[tag];

  const renderBody = () => {
    if (isLoading && !hasMarkers) {
      return (
        <div className="rounded border border-gray-200 bg-gray-50 p-4 text-sm text-gray-500">
          Loading {source === 'cost' ? 'cost' : 'figure'} markers…
        </div>
      );
    }

    if (!hasMarkers) {
      return PRESET_TABLE_EMPTY_STATE;
    }

    if (content.length === 0) return null;

    return (
      <TableComponent
        content={content}
        updateCell={updateCell}
        isInjectedCell={isInjectedCell}
      />
    );
  };

  return (
    <PresetTableShell label={label}>{renderBody()}</PresetTableShell>
  );
};

export default KidSetTableInput;
